import {
  CATALOG_ID_PREFIX,
  PLAN_PHASE,
  TRAINING_LIMITS,
  formatLoadParts,
  recommendedMinutes,
  type ExerciseCategory,
  type ExerciseLoad,
  type PlanPhase,
  type SessionPlanInput,
  type TrainingDiagram,
  type TrainingExerciseBriefDto,
  type TrainingExerciseDto,
  type TrainingPlanDto,
} from '@ukinfo/shared';

/**
 * Hilfen der Trainingsplanung: Anzeigetexte und der Entwurf einer Einheit.
 *
 * Der Entwurf haelt die Eintraege je Phase. Minuten bleiben bis zum Speichern
 * Text, damit ein Feld beim Tippen leer sein darf.
 */

export const TRAINING_PLANS_RESOURCE = 'sports_training_plans' as const;

/** Standardwert fuer die Dauer, wenn eine Uebung keinen Richtwert traegt. */
export const DEFAULT_ITEM_MINUTES = 10;

/** Altersklassen fuer Filter und Formular (U6 bis U21). */
export const AGE_CLASSES = Array.from({ length: 16 }, (_, index) => index + 6);

/** "U8 bis U12", "ab U10", "bis U12", "U10" oder null, wenn nichts angegeben ist. */
export function ageLabel(min: number | null, max: number | null): string | null {
  if (min !== null && max !== null) return min === max ? `U${min}` : `U${min} bis U${max}`;
  if (min !== null) return `ab U${min}`;
  if (max !== null) return `bis U${max}`;
  return null;
}

/** "6 bis 12 Spieler", "ab 6 Spieler" ... oder null. */
export function playersLabel(min: number | null, max: number | null): string | null {
  if (min !== null && max !== null) return min === max ? `${min} Spieler` : `${min} bis ${max} Spieler`;
  if (min !== null) return `ab ${min} Spielern`;
  if (max !== null) return `bis ${max} Spieler`;
  return null;
}

/**
 * Kurze Beschreibung einer Uebung fuer Listen: Alter, Spielerzahl, empfohlene Dauer ("10–15 Min.",
 * "4 × 3 Min., 1 Min. Pause (15 Min.)", siehe `formatLoadParts`).
 */
export function exerciseMeta(exercise: Pick<TrainingExerciseDto, 'ageMin' | 'ageMax' | 'playersMin' | 'playersMax'> & ExerciseLoad): string[] {
  return [
    ageLabel(exercise.ageMin, exercise.ageMax),
    playersLabel(exercise.playersMin, exercise.playersMax),
    ...formatLoadParts(exercise),
  ].filter((part): part is string => part !== null);
}

/** Die Empfehlung einer Uebung fuer den Entwurf; `null`, wenn sie weder eine Dauer noch Durchgaenge traegt. */
export function loadOf(exercise: ExerciseLoad): ExerciseLoad | null {
  const load: ExerciseLoad = {
    durationMinutes: exercise.durationMinutes ?? null,
    durationMaxMinutes: exercise.durationMaxMinutes ?? null,
    sets: exercise.sets ?? null,
    setMinutes: exercise.setMinutes ?? null,
    pauseMinutes: exercise.pauseMinutes ?? null,
  };
  return recommendedMinutes(load) === null ? null : load;
}

/** Schwerpunkte kommagetrennt eingeben, als Liste verschicken. */
export function parseTags(text: string): string[] {
  return text
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean);
}

/** Dauer eines Termins in Minuten; null bei fehlenden oder unsinnigen Zeiten. */
export function sessionMinutes(startAt: string | null | undefined, endAt: string | null | undefined): number | null {
  if (!startAt || !endAt) return null;
  const minutes = Math.round((new Date(endAt).getTime() - new Date(startAt).getTime()) / 60_000);
  return Number.isFinite(minutes) && minutes > 0 ? minutes : null;
}

/** Hinweis zur Planung gegen die Trainingsdauer, oder null, wenn alles passt. */
export function durationHint(total: number, planned: number | null): { tone: 'ok' | 'warning'; text: string } | null {
  if (planned === null) return null;
  const diff = planned - total;
  if (diff === 0) return { tone: 'ok', text: `Passt genau in die ${planned} Minuten des Trainings.` };
  if (diff < 0) return { tone: 'warning', text: `${-diff} Min. mehr als die ${planned} Minuten des Trainings.` };
  return { tone: 'warning', text: `${diff} Min. frei bei ${planned} Minuten Training.` };
}

/** Kennung, unter der sich die Uebung eines Eintrags laden laesst. */
export function itemExerciseId(item: { exerciseId: string | null; catalogKey: string | null }): string | null {
  if (item.exerciseId) return item.exerciseId;
  return item.catalogKey ? `${CATALOG_ID_PREFIX}${item.catalogKey}` : null;
}

/* ------------------------------------------------------------------ *
 * Entwurf einer Einheit
 * ------------------------------------------------------------------ */

export interface DraftItem {
  /** Nur fuer die Anzeige (React-Schluessel), wird nicht gespeichert. */
  key: string;
  exerciseId: string | null;
  catalogKey: string | null;
  title: string;
  minutes: string;
  note: string;
  /** Fuer die kleine Skizze im Editor. */
  category: ExerciseCategory | null;
  diagram: TrainingDiagram | null;
  /** Empfohlene Dauer der Uebung (nur Anzeige und Hinweis, wird nicht gespeichert); null bei freien Eintraegen. */
  load: ExerciseLoad | null;
}

export interface PlanDraft {
  title: string;
  focus: string;
  notes: string;
  items: Record<PlanPhase, DraftItem[]>;
}

let keyCounter = 0;
export function newKey(): string {
  keyCounter += 1;
  return `item-${keyCounter}`;
}

export function emptyDraft(): PlanDraft {
  return { title: '', focus: '', notes: '', items: { WARMUP: [], MAIN: [], FINAL: [] } };
}

export function draftFromPlan(plan: TrainingPlanDto): PlanDraft {
  const draft: PlanDraft = { title: plan.title, focus: plan.focus, notes: plan.notes, items: { WARMUP: [], MAIN: [], FINAL: [] } };
  for (const item of [...plan.items].sort((a, b) => a.position - b.position)) {
    draft.items[item.phase].push({
      key: newKey(),
      exerciseId: item.exerciseId,
      catalogKey: item.catalogKey,
      title: item.title,
      minutes: String(item.durationMinutes),
      note: item.note ?? '',
      category: item.exercise?.category ?? null,
      diagram: item.exercise?.diagram ?? null,
      load: item.exercise ? loadOf(item.exercise) : null,
    });
  }
  return draft;
}

/**
 * Ein Eintrag aus einer Uebung der Bibliothek (Katalog oder eigene). Als Minuten gelten die Gesamtdauer der
 * Durchgaenge, sonst das "bis" der Spanne, sonst "von", sonst die Vorgabe (`recommendedMinutes`).
 */
export function draftItemFromExercise(exercise: TrainingExerciseDto | TrainingExerciseBriefDto): DraftItem {
  const catalog = exercise.source === 'CATALOG';
  return {
    key: newKey(),
    exerciseId: catalog ? null : exercise.id,
    catalogKey: catalog ? exercise.id.slice(CATALOG_ID_PREFIX.length) : null,
    title: exercise.title,
    minutes: String(recommendedMinutes(exercise) ?? DEFAULT_ITEM_MINUTES),
    note: '',
    category: exercise.category,
    diagram: exercise.diagram,
    load: loadOf(exercise),
  };
}

export function freeDraftItem(): DraftItem {
  return { key: newKey(), exerciseId: null, catalogKey: null, title: '', minutes: String(DEFAULT_ITEM_MINUTES), note: '', category: null, diagram: null, load: null };
}

/** Minuten eines Eintrags; unlesbare oder fehlende Werte zaehlen als 0. */
export function itemMinutes(item: DraftItem): number {
  const value = Number(item.minutes);
  return Number.isInteger(value) && value > 0 ? value : 0;
}

export function phaseMinutes(items: DraftItem[]): number {
  return items.reduce((sum, item) => sum + itemMinutes(item), 0);
}

export function draftTotalMinutes(draft: PlanDraft): number {
  return PLAN_PHASE.reduce((sum, phase) => sum + phaseMinutes(draft.items[phase]), 0);
}

export function draftItemCount(draft: PlanDraft): number {
  return PLAN_PHASE.reduce((sum, phase) => sum + draft.items[phase].length, 0);
}

/** Verschiebt einen Eintrag innerhalb seiner Phase um eine Stelle. */
export function moveItem<T>(list: T[], index: number, direction: -1 | 1): T[] {
  const target = index + direction;
  if (target < 0 || target >= list.length) return list;
  const next = [...list];
  const [moved] = next.splice(index, 1);
  next.splice(target, 0, moved as T);
  return next;
}

/** Stand zum Vergleichen: ohne die reinen Anzeigefelder. */
export function draftSignature(draft: PlanDraft): string {
  return JSON.stringify({
    title: draft.title,
    focus: draft.focus,
    notes: draft.notes,
    items: PLAN_PHASE.map((phase) =>
      draft.items[phase].map((item) => [item.exerciseId, item.catalogKey, item.title, item.minutes, item.note]),
    ),
  });
}

/** Fehler je Eintrag (Schluessel = Eintragsschluessel), leer wenn der Entwurf speicherbar ist. */
export function validateDraft(draft: PlanDraft, titleRequired: boolean): { title?: string; items: Record<string, string>; general?: string } {
  const result: { title?: string; items: Record<string, string>; general?: string } = { items: {} };
  if (titleRequired && !draft.title.trim()) result.title = 'Bitte gib einen Namen an.';
  for (const phase of PLAN_PHASE) {
    for (const item of draft.items[phase]) {
      const minutes = Number(item.minutes);
      if (!item.title.trim()) result.items[item.key] = 'Bitte gib eine Bezeichnung an.';
      else if (!Number.isInteger(minutes) || minutes < 1 || minutes > TRAINING_LIMITS.minutes) {
        result.items[item.key] = `Minuten zwischen 1 und ${TRAINING_LIMITS.minutes}.`;
      }
    }
  }
  if (draftItemCount(draft) > TRAINING_LIMITS.items) result.general = `Höchstens ${TRAINING_LIMITS.items} Einträge je Einheit.`;
  return result;
}

export function hasErrors(errors: ReturnType<typeof validateDraft>): boolean {
  return Boolean(errors.title) || Boolean(errors.general) || Object.keys(errors.items).length > 0;
}

/** Der Entwurf als Nutzlast fuer Server (Reihenfolge = Reihenfolge der Phasen und Eintraege). */
export function draftToInput(draft: PlanDraft): SessionPlanInput {
  return {
    title: draft.title.trim(),
    focus: draft.focus.trim(),
    notes: draft.notes.trim(),
    items: PLAN_PHASE.flatMap((phase) =>
      draft.items[phase].map((item) => ({
        phase,
        exerciseId: item.exerciseId,
        catalogKey: item.catalogKey,
        title: item.title.trim(),
        durationMinutes: Number(item.minutes),
        note: item.note.trim() || null,
      })),
    ),
  };
}
